import { useCallback } from 'react';

/**
 * A React hook that returns memoized handlers for managing selection ranges on charts.
 *
 * This hook wraps two externally provided functions:
 * - `addSelectedRange`: to add a new time range selection to a specific chart.
 * - `removeSelectedRange`: to remove an existing selection by its ID (usually applied across all charts).
 *
 * Useful for scenarios like:
 * - Interactive chart dashboards where users select time ranges for comparison or highlighting.
 * - Managing synchronized interactions across multiple chart views.
 *
 * @param addSelectedRange - A function to add a new selection (startTime, endTime, chartId).
 * @param removeSelectedRange - A function to remove a selection by its ID.
 *
 * @returns An object with two handler functions:
 * - `handleAddSelectedRange`: Adds a new range to a chart.
 * - `handleRemoveSelectedRange`: Removes a range by ID (typically across all charts).
 */
export function useSelectionHandlers(
  // eslint-disable-next-line @typescript-eslint/ban-types
  addSelectedRange: Function,
  // eslint-disable-next-line @typescript-eslint/ban-types
  removeSelectedRange: Function
) {
  /**
   * Adds a selected range (start and end time) to the specified chart.
   *
   * @param startTime - Start timestamp of the selection.
   * @param endTime - End timestamp of the selection.
   * @param chartId - Identifier for the chart the range applies to.
   */
  const handleAddSelectedRange = useCallback(
    (startTime: Date, endTime: Date, chartId: string) => {
      addSelectedRange(startTime, endTime, chartId);
    },
    [addSelectedRange]
  );

  /**
   * Removes a selected range by its ID.
   * This is typically used to remove selections globally.
   *
   * @param id - The identifier of the range to remove.
   */
  const handleRemoveSelectedRange = useCallback(
    (id: string) => {
      removeSelectedRange(id);
    },
    [removeSelectedRange]
  );

  return { handleAddSelectedRange, handleRemoveSelectedRange };
}
